// src/routes/PrivateRoute.js
import React, {useEffect} from 'react';
import { useDispatch } from "react-redux";
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAuthToken, selectAuthIsAdmin, selectAuthIsForecaster, selectManagedResourcesAuth } from '../slices/authSlice';
import { fetchResources, selectResourcesLoaded, selectResources } from '../slices/userSlice';
import {selectSelectedResourceId, setSelectedResource} from "../slices/preferencesSlice";
import { scopeResourcesToRole, shouldScopeToManaged } from '../utils/resourceScope';


const PrivateRoute = () => {

  const dispatch = useDispatch();
  const location = useLocation();
  const token = useSelector(selectAuthToken);
  const resourcesLoaded = useSelector(selectResourcesLoaded);
  const resources = useSelector(selectResources);
  const selectedResourceId = useSelector(selectSelectedResourceId);
  const isAdmin = useSelector(selectAuthIsAdmin);
  const isForecaster = useSelector(selectAuthIsForecaster);
  const managedResources = useSelector(selectManagedResourcesAuth);

  useEffect(() => {
    // If resources not loaded, fetch them (only once authenticated)
    if (token && !resourcesLoaded) {
      dispatch(fetchResources());
    }
  }, [dispatch, token, resourcesLoaded]);

  useEffect(() => {
    if (!resourcesLoaded || resources.length === 0) return;
    const scoped = shouldScopeToManaged({ isAdmin, isForecaster });
    // Market owners: wait until the managed-resource list has loaded before
    // choosing/validating a default, otherwise we'd fall back to an unowned
    // resource during the bootstrap race.
    if (scoped && (managedResources?.length ?? 0) === 0) return;
    const visible = scopeResourcesToRole(resources, {
      managedResources, isAdmin, isForecaster,
    });
    if (visible.length === 0) return;
    // Reset when nothing is selected or the selection is outside the visible
    // set (e.g. a stale market owner selection pointing at an unowned resource).
    const selectionValid = selectedResourceId
      && visible.some((r) => r.id === selectedResourceId);
    if (!selectionValid) {
      dispatch(setSelectedResource(visible[0].id));
    }
  }, [dispatch, resourcesLoaded, resources, selectedResourceId, managedResources, isAdmin, isForecaster]);

  // Auth gate FIRST: unauthenticated visitors go to the sign-in page carrying
  // the URL they asked for (path + query + hash), so SignIn can send them back
  // after login. The old order gated on selectedResourceId and rendered a
  // blank page for first-time unauthenticated visitors.
  if (!token) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // Authenticated but resource bootstrap still in flight: render nothing
  // (matches previous behavior while resources/selection load).
  if (!selectedResourceId) {
    return null;
  }

  return <Outlet />;
};

export default PrivateRoute;
